import React, { useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  ArrowLeft, Camera, Save, Loader2, CheckCircle,
  AlertCircle, Eye, EyeOff, User, Mail, Lock,
} from 'lucide-react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { updateEditorProfile, updateEditorPassword } from '../../utils/api';


const content = {
  ne: {
    pageTitle: 'प्रोफाइल सेटिङ',
    pageSub: 'आफ्नो खाता विवरण अपडेट गर्नुहोस्',
    profileSection: 'व्यक्तिगत जानकारी',
    passwordSection: 'पासवर्ड परिवर्तन गर्नुहोस्',
    nameLabel: 'पूरा नाम',
    namePlaceholder: 'आफ्नो नाम लेख्नुहोस्',
    emailLabel: 'इमेल ठेगाना',
    emailPlaceholder: 'your@email.com',
    currentPassword: 'हालको पासवर्ड',
    newPassword: 'नयाँ पासवर्ड',
    confirmPassword: 'पासवर्ड पुष्टि गर्नुहोस्',
    saveProfile: 'जानकारी सुरक्षित गर्नुहोस्',
    savePassword: 'पासवर्ड परिवर्तन गर्नुहोस्',
    back: 'फर्कनुहोस्',
    changePhoto: 'फोटो बदल्नुहोस्',
    required: 'यो फिल्ड आवश्यक छ',
    passwordMismatch: 'पासवर्ड मेल खाएन',
    passwordMinLength: 'पासवर्ड कम्तिमा ८ अक्षर हुनुपर्छ',
    profileSaved: 'प्रोफाइल अपडेट भयो!',
    passwordChanged: 'पासवर्ड परिवर्तन भयो!',
    invalidEmail: 'मान्य इमेल ठेगाना लेख्नुहोस्',
  },
  en: {
    pageTitle: 'Profile Settings',
    pageSub: 'Update your account details',
    profileSection: 'Personal Information',
    passwordSection: 'Change Password',
    nameLabel: 'Full Name',
    namePlaceholder: 'Enter your name',
    emailLabel: 'Email Address',
    emailPlaceholder: 'your@email.com',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    confirmPassword: 'Confirm New Password',
    saveProfile: 'Save Profile',
    savePassword: 'Update Password',
    back: 'Back',
    changePhoto: 'Change Photo',
    required: 'This field is required',
    passwordMismatch: 'Passwords do not match',
    passwordMinLength: 'Password must be at least 8 characters',
    profileSaved: 'Profile updated!',
    passwordChanged: 'Password changed!',
    invalidEmail: 'Enter a valid email address',
  },
};


const initials = (name) =>
  (name || '?').split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('');

// ── Defined OUTSIDE component so they never remount on re-render ──────────────
const Field = ({ label, error, children }) => (
  <div>
    <label className="mb-1.5 block text-xs font-extrabold text-char-500">{label}</label>
    {children}
    {error && <p className="mt-1 text-xs font-bold text-red-500">{error}</p>}
  </div>
);

const inputBase = (err) =>
  `w-full rounded-2xl border px-4 py-2.5 text-sm text-char-800 placeholder-char-300 transition-colors focus:outline-none ${
    err ? 'border-red-300 focus:border-red-400' : 'border-char-200 focus:border-primary'
  }`;

// ─────────────────────────────────────────────────────────────────────────────

const ProfileSettings = () => {
  const {language = "ne"} = useOutletContext() || {};
  const t = content[language] || content.en;
  const navigate = useNavigate();
  const { user, updateUser, logout } = useAuth();
  const fileRef = useRef(null);

  const [profile, setProfile] = useState({
    name: user?.name || '',
    email: user?.email || '',
  });
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar || null);
  const [avatarFile, setAvatarFile] = useState(null);

  const [passwords, setPasswords] = useState({ current: '', newPass: '', confirm: '' });
  const [showPw, setShowPw] = useState({ current: false, newPass: false, confirm: false });

  const [profileErrors, setProfileErrors] = useState({});
  const [passwordErrors, setPasswordErrors] = useState({});
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setAvatarPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const validateProfile = () => {
    const e = {};
    if (!profile.name.trim()) e.name = t.required;
    if (!profile.email.trim()) e.email = t.required;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email)) e.email = t.invalidEmail;
    setProfileErrors(e);
    return Object.keys(e).length === 0;
  };

  const validatePasswords = () => {
    const e = {};
    if (!passwords.current) e.current = t.required;
    if (!passwords.newPass) e.newPass = t.required;
    else if (passwords.newPass.length < 8) e.newPass = t.passwordMinLength;
    if (!passwords.confirm) e.confirm = t.required;
    else if (passwords.newPass !== passwords.confirm) e.confirm = t.passwordMismatch;
    setPasswordErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSaveProfile = async () => {
    if (!validateProfile()) return;
    setProfileLoading(true);
    try {
      const formData = new FormData();
      formData.append('name', profile.name);
      formData.append('email', profile.email);
      if (avatarFile) formData.append('avatar', avatarFile);
      const res = await updateEditorProfile(formData);
      if (res?.data) updateUser(res.data);
      showToast('success', t.profileSaved);
    } catch (err) {
      showToast('error', err?.response?.data?.message || err?.message || 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleSavePassword = async () => {
  if (!validatePasswords()) return;
  setPasswordLoading(true);
  try {
    await updateEditorPassword({ currentPassword: passwords.current, newPassword: passwords.newPass });
    setPasswords({ current: '', newPass: '', confirm: '' });
    showToast('success', t.passwordChanged);
    setTimeout(() => {
      logout();
      navigate('/login');
    }, 1500);
  } catch (err) {
    showToast('error', err?.response?.data?.message || err?.message || 'Failed to update password');
  } finally {
    setPasswordLoading(false);
  }
};

  return (
    <div className="mx-auto w-full max-w-2xl space-y-6 pb-12">
      {/* Toast */}
      {toast && (
        <div
          className={`fixed right-5 top-5 z-50 flex items-center gap-2.5 rounded-2xl px-4 py-3 text-sm font-bold text-white shadow-xl`}
          style={{
            backgroundColor: toast.type === 'success' ? '#16a34a' : '#ef4444',
            animation: 'fadeSlideIn 150ms ease-out',
          }}
        >
          {toast.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl border border-char-200 text-char-500 transition-colors hover:bg-char-50"
        >
          <ArrowLeft size={17} />
        </button>
        <div>
          <h2 className="text-2xl font-extrabold text-char-900">{t.pageTitle}</h2>
          <p className="mt-0.5 text-sm text-char-400">{t.pageSub}</p>
        </div>
      </div>

      {/* Avatar */}
      <div className="flex items-center gap-5 rounded-3xl border border-char-100 bg-white p-6 shadow-sm">
        <div className="relative">
          <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full bg-primary-50">
            {avatarPreview
              ? <img src={avatarPreview} alt="Avatar" className="h-full w-full object-cover" />
              : <span className="text-2xl font-extrabold text-primary">{initials(profile.name)}</span>
            }
          </div>
          <button
            onClick={() => fileRef.current?.click()}
            className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full border-2 border-white shadow-md"
            style={{ backgroundColor: '#16a34a' }}
          >
            <Camera size={13} className="text-white" />
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} />
        </div>
        <div>
          <p className="text-base font-extrabold text-char-900">{profile.name || 'Editor'}</p>
          <p className="text-sm text-char-400">{profile.email}</p>
          <button onClick={() => fileRef.current?.click()} className="mt-1.5 text-xs font-bold text-primary hover:underline">
            {t.changePhoto}
          </button>
        </div>
      </div>

      {/* Personal Info */}
      <div className="rounded-3xl border border-char-100 bg-white p-6 shadow-sm space-y-5">
        <h3 className="text-sm font-extrabold text-char-900">{t.profileSection}</h3>

        <Field label={t.nameLabel} error={profileErrors.name}>
          <div className="relative">
            <User size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-char-300" />
            <input
              type="text"
              value={profile.name}
              onChange={(e) => {
                setProfile((p) => ({ ...p, name: e.target.value }));
                if (profileErrors.name) setProfileErrors((p) => ({ ...p, name: undefined }));
              }}
              placeholder={t.namePlaceholder}
              className={`${inputBase(profileErrors.name)} pl-10`}
            />
          </div>
        </Field>

        <Field label={t.emailLabel} error={profileErrors.email}>
          <div className="relative">
            <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-char-300" />
            <input
              type="email"
              value={profile.email}
              onChange={(e) => {
                setProfile((p) => ({ ...p, email: e.target.value }));
                if (profileErrors.email) setProfileErrors((p) => ({ ...p, email: undefined }));
              }}
              placeholder={t.emailPlaceholder}
              className={`${inputBase(profileErrors.email)} pl-10`}
            />
          </div>
        </Field>

        <div className="flex justify-end">
          <button
            onClick={handleSaveProfile}
            disabled={profileLoading}
            className="flex items-center gap-2 rounded-2xl px-5 py-2.5 text-sm font-extrabold text-white disabled:opacity-60"
            style={{ backgroundColor: '#16a34a' }}
          >
            {profileLoading ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {t.saveProfile}
          </button>
        </div>
      </div>

      {/* Change Password */}
      <div className="rounded-3xl border border-char-100 bg-white p-6 shadow-sm space-y-5">
        <h3 className="text-sm font-extrabold text-char-900">{t.passwordSection}</h3>

        {['current', 'newPass', 'confirm'].map((fieldKey) => (
          <Field
            key={fieldKey}
            label={fieldKey === 'current' ? t.currentPassword : fieldKey === 'newPass' ? t.newPassword : t.confirmPassword}
            error={passwordErrors[fieldKey]}
          >
            <div className="relative">
              <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-char-300" />
              <input
                type={showPw[fieldKey] ? 'text' : 'password'}
                value={passwords[fieldKey]}
                onChange={(e) => {
                  setPasswords((p) => ({ ...p, [fieldKey]: e.target.value }));
                  if (passwordErrors[fieldKey]) setPasswordErrors((p) => ({ ...p, [fieldKey]: undefined }));
                }}
                placeholder="••••••••"
                className={`${inputBase(passwordErrors[fieldKey])} pl-10 pr-10`}
              />
              <button
                type="button"
                onClick={() => setShowPw((p) => ({ ...p, [fieldKey]: !p[fieldKey] }))}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-char-300 hover:text-char-500"
              >
                {showPw[fieldKey] ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </Field>
        ))}

        <div className="flex justify-end">
          <button
            onClick={handleSavePassword}
            disabled={passwordLoading}
            className="flex items-center gap-2 rounded-2xl px-5 py-2.5 text-sm font-extrabold text-white disabled:opacity-60"
            style={{ backgroundColor: '#16a34a' }}
          >
            {passwordLoading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
            {t.savePassword}
          </button>
        </div>
      </div>

      <style>{`
  @keyframes fadeSlideIn {
    from { opacity: 0; transform: translateY(-6px); }
    to { opacity: 1; transform: translateY(0); }
  }
  input[type="password"]::-ms-reveal,
  input[type="password"]::-ms-clear {
    display: none;
  }
  input[type="password"]::-webkit-credentials-auto-fill-button,
  input[type="password"]::-webkit-strong-password-auto-fill-button {
    display: none !important;
    visibility: hidden;
  }
`}</style>
    </div>
  );
};

export default ProfileSettings;